import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { TABLE_ICONS } from '../utils/tableIcons';
import { useSnackbar } from '../hooks/useSnackbar';
import Snackbar from '../components/Snackbar';
import './Stickers.css';

const Stickers = () => {
  // Snackbar hook
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  // Helper function to safely trigger file input clicks
  const triggerFileInput = (selector) => {
    const input = document.querySelector(selector);
    if (input) {
      input.click();
    } else {
      console.warn(`File input with selector "${selector}" not found`);
    }
  };

  const [stickers, setStickers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStickerForm, setShowStickerForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSticker, setEditingSticker] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryStickers, setCategoryStickers] = useState([]);
  const [categoryFormData, setCategoryFormData] = useState({
    name: ''
  });
  const [stickerFormData, setStickerFormData] = useState({
    categoryId: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // 'category' or 'sticker'
  const [showGeneralStickerUpload, setShowGeneralStickerUpload] = useState(false);
  const [generalUploadFormData, setGeneralUploadFormData] = useState({
    categoryId: ''
  });
  const [showQuickStickerUpload, setShowQuickStickerUpload] = useState(false);
  const [quickUploadCategory, setQuickUploadCategory] = useState(null);
  const [quickUploadFiles, setQuickUploadFiles] = useState([]);

  useEffect(() => {
    fetchCategoriesAndStickers();
  }, []);

  const fetchCategoriesAndStickers = async () => {
    try {
      setLoading(true);

      // Fetch all category documents from Stickers collection
      const querySnapshot = await getDocs(collection(db, 'Stickers'));
      
      // Process all categories in parallel for faster loading
      const categoriesPromises = querySnapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();

        // Count stickers in the upload subcollection
        let stickerCount = 0;
        const stickersList = [];
        
        try {
          const uploadsSnapshot = await getDocs(collection(db, 'Stickers', docSnap.id, 'upload'));
          stickerCount = uploadsSnapshot.size;
          
          // Also collect sticker data for the stickers state
          uploadsSnapshot.forEach((uploadDoc) => {
            const uploadData = uploadDoc.data();
            if (uploadData.downloadURL) {
              stickersList.push({
                id: uploadDoc.id,
                createdAt: uploadData.createdAt,
                downloadURL: uploadData.downloadURL,
                fileName: uploadData.fileName || '',
                fileSize: uploadData.fileSize || 0,
                fileType: uploadData.fileType || '',
                name: uploadData.name || '',
                categoryId: docSnap.id,
                updatedAt: uploadData.updatedAt || uploadData.createdAt
              });
            }
          });
        } catch (error) {
          console.warn('Error counting stickers for category:', docSnap.id, error);
        }
        
        return {
          category: {
            id: docSnap.id,
            name: data.name || '',
            createdAt: data.createdAt || '',
            stickerCount: stickerCount
          },
          stickers: stickersList
        };
      });
      
      const results = await Promise.all(categoriesPromises);
      
      // Extract categories and stickers from results
      const categoriesList = results.map(result => result.category);
      const allStickers = results.flatMap(result => result.stickers);
      
      // Sort categories by createdAt (newest first)
      categoriesList.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          const aDate = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const bDate = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return bDate - aDate;
        }
        // Fallback to name sorting if no createdAt
        return a.name.localeCompare(b.name);
      });
      
      // Sort stickers by createdAt (newest first)
      allStickers.sort((a, b) => {
        const aDate = new Date(a.createdAt);
        const bDate = new Date(b.createdAt);
        return bDate - aDate;
      });

      setCategories(categoriesList);
      setStickers(allStickers);
    } catch (error) {
      console.error('Error fetching categories and stickers:', error);
      setCategories([]);
      setStickers([]);
    } finally {
      setLoading(false);
    }
  };

  // Legacy functions for backward compatibility (used by upload functions)
  const fetchStickers = async () => {
    // This function is kept for compatibility with upload functions
    // The main loading is now handled by fetchCategoriesAndStickers
    await fetchCategoriesAndStickers();
  };

  const fetchCategories = async () => {
    // This function is kept for compatibility with upload functions
    // The main loading is now handled by fetchCategoriesAndStickers
    await fetchCategoriesAndStickers();
  };

  const handlePreview = async (category) => {
    setSelectedCategory(category);
    const categoryStickers = stickers.filter(sticker => sticker.categoryId === category.id);
    setCategoryStickers(categoryStickers);
    setShowPreviewModal(true);
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({ name: '' });
    setShowCategoryForm(true);
  };

  const handleDeleteCategoryClick = (category) => {
    setItemToDelete(category);
    setDeleteType('category');
    setShowDeleteConfirm(true);
  };

  const handleDeleteCategory = async () => {
    if (!itemToDelete) return;
    
    try {
      await deleteDoc(doc(db, 'Stickers', itemToDelete.id));
      await fetchCategories();
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      showSnackbar(`Category "${itemToDelete.name}" deleted successfully`, 'success');
    } catch (error) {
      console.error('Error deleting category:', error);
      showSnackbar('Error deleting category. Please try again.', 'error');
    }
  };

  const handleDeleteStickerClick = (sticker) => {
    setItemToDelete(sticker);
    setDeleteType('sticker');
    setShowDeleteConfirm(true);
  };

  const handleDeleteSticker = async () => {
    if (!itemToDelete) return;
    
    try {
      // Delete from the upload subcollection
      await deleteDoc(doc(db, 'Stickers', itemToDelete.categoryId, 'upload', itemToDelete.id));
      await fetchStickers();
      // Also refresh category stickers if we're in preview mode
      if (selectedCategory) {
        const categoryStickers = stickers.filter(sticker => sticker.categoryId === selectedCategory.id);
        setCategoryStickers(categoryStickers);
      }
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      showSnackbar(`Sticker "${itemToDelete.name}" deleted successfully`, 'success');
    } catch (error) {
      console.error('Error deleting sticker:', error);
      showSnackbar('Error deleting sticker. Please try again.', 'error');
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteType === 'category') {
      handleDeleteCategory();
    } else if (deleteType === 'sticker') {
      handleDeleteSticker();
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
    setDeleteType('');
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({ name: category.name });
    setShowCategoryForm(true);
  };

  const handleAddSticker = (categoryId) => {
    setStickerFormData({ categoryId: categoryId || '' });
    setSelectedFiles([]);
    setFilePreviews([]);
    setShowStickerForm(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        // Update existing category
        await updateDoc(doc(db, 'Stickers', editingCategory.id), {
          name: categoryFormData.name,
          updatedAt: Timestamp.now()
        });
      } else {
        // Add new category
        await addDoc(collection(db, 'Stickers'), {
          name: categoryFormData.name,
          createdAt: Timestamp.now()
        });
      }
      
      await fetchCategories();
      setShowCategoryForm(false);
      setCategoryFormData({ name: '' });
      
      // Show success message
      if (editingCategory) {
        showSnackbar(`Category "${categoryFormData.name}" updated successfully`, 'success');
      } else {
        showSnackbar(`Category "${categoryFormData.name}" created successfully`, 'success');
      }
      
      setEditingCategory(null);
    } catch (error) {
      console.error('Error saving category:', error);
      showSnackbar('Error saving category. Please try again.', 'error');
    }
  };

  const uploadFile = async (file) => {
    if (!file) return null;
    
    try {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `Stickers/${fileName}`);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        downloadURL,
        fileName,
        fileSize: file.size,
        fileType: file.type,
        name: file.name
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleStickerSubmit = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      showSnackbar('Please select at least one sticker file', 'warning');
      return;
    }

    try {
      setUploading(true);
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const fileData = await uploadFile(file);
        
        const stickerData = {
          createdAt: Timestamp.now(),
          name: file.name.split('.')[0],
          updatedAt: Timestamp.now(),
          downloadURL: fileData.downloadURL,
          fileName: fileData.fileName,
          fileSize: fileData.fileSize,
          fileType: fileData.fileType
        };

        // Add to the upload subcollection of the selected category
        const docRef = await addDoc(collection(db, 'Stickers', stickerFormData.categoryId, 'upload'), stickerData);
        return { id: docRef.id, ...stickerData };
      });

      const newStickers = await Promise.all(uploadPromises);
      setStickers([...newStickers, ...stickers]);
      
      // Update category stickers if we're in preview mode
      if (selectedCategory && stickerFormData.categoryId === selectedCategory.id) {
        setCategoryStickers([...newStickers, ...categoryStickers]);
      }

      setShowStickerForm(false);
      setStickerFormData({ categoryId: '' });
      setSelectedFiles([]);
      setFilePreviews([]);
      setEditingSticker(null);
    } catch (error) {
      console.error('Error saving stickers:', error);
      showSnackbar('Error saving stickers. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      handleFileSelection(imageFiles);
    }
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleFileSelection(files);
  };

  const handleFileSelection = (files) => {
    setSelectedFiles(files);
    
    // Create previews for images
    const previewPromises = files.map(file => {
      return new Promise((resolve) => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => resolve({ file, preview: e.target.result });
          reader.readAsDataURL(file);
        } else {
          resolve({ file, preview: null });
        }
      });
    });
    
    Promise.all(previewPromises).then(setFilePreviews);
  };

  const handleGeneralStickerUpload = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      showSnackbar('Please select at least one sticker file', 'warning');
      return;
    }

    if (!generalUploadFormData.categoryId) {
      showSnackbar('Please select a category', 'warning');
      return;
    }

    try {
      setUploading(true);
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const fileData = await uploadFile(file);
        
        const stickerData = {
          createdAt: Timestamp.now(),
          name: file.name.split('.')[0],
          updatedAt: Timestamp.now(),
          downloadURL: fileData.downloadURL,
          fileName: fileData.fileName,
          fileSize: fileData.fileSize,
          fileType: fileData.fileType
        };

        // Add to the upload subcollection of the selected category
        const docRef = await addDoc(collection(db, 'Stickers', generalUploadFormData.categoryId, 'upload'), stickerData);
        return { id: docRef.id, ...stickerData, categoryId: generalUploadFormData.categoryId };
      });

      const newStickers = await Promise.all(uploadPromises);
      setStickers([...newStickers, ...stickers]);
      
      // Update category stickers if we're in preview mode and it's the same category
      if (selectedCategory && generalUploadFormData.categoryId === selectedCategory.id) {
        setCategoryStickers([...newStickers, ...categoryStickers]);
      }

      // Reset form and close modal
      setShowGeneralStickerUpload(false);
      setGeneralUploadFormData({ categoryId: '' });
      setSelectedFiles([]);
      setFilePreviews([]);
      
      showSnackbar(`Successfully uploaded ${newStickers.length} sticker(s)!`, 'success');
    } catch (error) {
      console.error('Error saving stickers:', error);
      showSnackbar('Error saving stickers. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleQuickStickerUpload = async (e) => {
    e.preventDefault();
    if (quickUploadFiles.length === 0) {
      showSnackbar('Please select at least one sticker file', 'warning');
      return;
    }

    if (!quickUploadCategory) {
      showSnackbar('No category selected', 'warning');
      return;
    }

    try {
      setUploading(true);
      const uploadPromises = quickUploadFiles.map(async (file, index) => {
        const fileData = await uploadFile(file);
        
        const stickerData = {
          createdAt: Timestamp.now(),
          name: file.name.split('.')[0],
          updatedAt: Timestamp.now(),
          downloadURL: fileData.downloadURL,
          fileName: fileData.fileName,
          fileSize: fileData.fileSize,
          fileType: fileData.fileType
        };

        // Add to the upload subcollection of the selected category
        const docRef = await addDoc(collection(db, 'Stickers', quickUploadCategory.id, 'upload'), stickerData);
        return { id: docRef.id, ...stickerData, categoryId: quickUploadCategory.id };
      });

      const newStickers = await Promise.all(uploadPromises);
      setStickers([...newStickers, ...stickers]);
      
      // Update category stickers if we're in preview mode and it's the same category
      if (selectedCategory && quickUploadCategory.id === selectedCategory.id) {
        setCategoryStickers([...newStickers, ...categoryStickers]);
      }

      // Reset form and close modal
      setShowQuickStickerUpload(false);
      setQuickUploadCategory(null);
      setQuickUploadFiles([]);
      
      showSnackbar(`Successfully uploaded ${newStickers.length} sticker(s) to ${quickUploadCategory.name}!`, 'success');
    } catch (error) {
      console.error('Error saving stickers:', error);
      showSnackbar('Error saving stickers. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = filePreviews.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setFilePreviews(newPreviews);
  };

  if (loading) {
    return (
      <div className="stickers-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stickers-page">
      <div className="page-header stickers-header">
        <h2>Stickers</h2>
        <div className="stickers-header-actions">
          <button onClick={handleAddCategory} className="btn btn-secondary">
            Add Category
          </button>
          <button onClick={() => setShowGeneralStickerUpload(true)} className="btn btn-primary">
            Add Sticker Upload
          </button>
        </div>
      </div>

      <div className="stickers-table-container">
        <table className="stickers-table">
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Subcategory Name</th>
              <th>Design Count</th>
              <th>Add Design</th>
              <th>Preview</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category, index) => (
              <tr key={category.id}>
                <td>{index + 1}</td>
                <td>{category.name}</td>
                <td>{category.stickerCount || 0}</td>
                <td>
                  <button 
                    onClick={() => {
                      setQuickUploadCategory(category);
                      setQuickUploadFiles([]);
                      setShowQuickStickerUpload(true);
                    }}
                    className="btn-icon"
                    title="Quick Upload to this category"
                  >
                    {TABLE_ICONS.ADD}
                  </button>
                </td>
                <td>
                  <button 
                    onClick={() => handlePreview(category)}
                    className="btn-icon"
                    title="Preview stickers"
                  >
                    {TABLE_ICONS.VIEW}
                  </button>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      onClick={() => handleEditCategory(category)}
                      className="btn-icon"
                      title="Edit Category"
                    >
                      {TABLE_ICONS.EDIT}
                    </button>
                    <button 
                      onClick={() => handleDeleteCategoryClick(category)}
                      className="btn-icon"
                      title="Delete Category"
                    >
                      {TABLE_ICONS.DELETE}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {categories.length === 0 && !loading && (
        <div className="no-categories">
          <div className="no-categories-icon">📂</div>
          <h3>No Categories Found</h3>
          <p>Start by creating your first sticker category.</p>
        </div>
      )}

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="modal-overlay">
          <div className="modal-content add-category-modal">
            <div className="modal-header">
              <h3>{editingCategory ? 'Edit Category' : 'Add Category'}</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowCategoryForm(false);
                  setCategoryFormData({ name: '' });
                  setEditingCategory(null);
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleCategorySubmit}>
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  placeholder="Enter category name"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({...categoryFormData, name: e.target.value})}
                  required
                  autoFocus
                />
              </div>
              
              <div className="form-actions">
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCategoryForm(false);
                    setCategoryFormData({ name: '' });
                    setEditingCategory(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                >
                  {editingCategory ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Stickers in {selectedCategory?.name}</h2>
              <button 
                className="close-btn"
                onClick={() => setShowPreviewModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="stickers-grid-preview">
              {categoryStickers.length === 0 ? (
                <div className="no-stickers">
                  <p>No stickers in this category yet.</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleAddSticker(selectedCategory?.id)}
                  >
                    Add First Sticker
                  </button>
                </div>
              ) : (
                categoryStickers.map(sticker => (
                  <div key={sticker.id} className="sticker-preview-card">
                    <div className="sticker-image">
                      <img src={sticker.downloadURL} alt={sticker.name} />
                    </div>
                    <div className="sticker-info">
                      <h4>{sticker.name}</h4>
                      <div className="sticker-actions">
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteStickerClick(sticker)}
                        >
                          {TABLE_ICONS.DELETE}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Sticker Modal */}
      {showStickerForm && (
        <div className="modal-overlay">
          <div className="modal-content add-sticker-modal">
            <div className="modal-header">
              <h2>Add Stickers</h2>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowStickerForm(false);
                  setSelectedFiles([]);
                  setFilePreviews([]);
                  setEditingSticker(null);
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleStickerSubmit}>
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={stickerFormData.categoryId}
                  onChange={(e) => setStickerFormData({...stickerFormData, categoryId: e.target.value})}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Sticker Files *</label>
                <div 
                  className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  {filePreviews.length > 0 ? (
                    <div className="file-previews">
                      {filePreviews.map((item, index) => (
                        <div key={index} className="file-preview-item">
                          {item.preview ? (
                            <img src={item.preview} alt={`Preview ${index}`} />
                          ) : (
                            <div className="file-placeholder">
                              📁 {item.file.name}
                            </div>
                          )}
                          <button 
                            type="button" 
                            className="remove-file-btn"
                            onClick={() => removeFile(index)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <div className="add-more-files">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleFileInput}
                          className="file-input"
                          id="additional-files"
                        />
                        <label htmlFor="additional-files" className="btn btn-sm btn-secondary">
                          + Add More
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <div className="upload-icon">🎨</div>
                      <p>Drag and drop sticker images here, or click to select</p>
                      <p className="upload-hint">You can select multiple files at once</p>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileInput}
                        className="file-input"
                        id="sticker-files"
                      />
                      <label htmlFor="sticker-files" className="btn btn-primary">
                        Choose Files
                      </label>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowStickerForm(false);
                    setSelectedFiles([]);
                    setFilePreviews([]);
                    setEditingSticker(null);
                    setStickerFormData({ categoryId: '' });
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                  disabled={uploading || selectedFiles.length === 0}
                >
                  {uploading ? 'UPLOADING...' : `UPLOAD ${selectedFiles.length} STICKER${selectedFiles.length !== 1 ? 'S' : ''}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-confirm-modal">
            <div className="delete-modal-header">
              <h3>Confirm Delete</h3>
              <button 
                className="close-btn"
                onClick={handleDeleteCancel}
              >
                ×
              </button>
            </div>
            
            <div className="delete-modal-content">
              <div className="warning-icon">
                {TABLE_ICONS.WARNING}
              </div>
              
              <div className="delete-message">
                <p>
                  {deleteType === 'category' 
                    ? `Are you sure you want to delete the category "${itemToDelete?.name}"? All stickers in this category will also be affected.`
                    : `Are you sure you want to delete the sticker "${itemToDelete?.name}"?`
                  }
                </p>
                <p className="warning-text">This action cannot be undone.</p>
              </div>
            </div>
            
            <div className="delete-modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={handleDeleteCancel}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
              >
                {deleteType === 'category' ? 'Delete Category' : 'Delete Sticker'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Sticker Upload Modal */}
      {showQuickStickerUpload && quickUploadCategory && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Quick Upload to {quickUploadCategory.name}</h3>
              <button 
                onClick={() => {
                  setShowQuickStickerUpload(false);
                  setQuickUploadCategory(null);
                  setQuickUploadFiles([]);
                }}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <div className="upload-modal-content">
              <form onSubmit={handleQuickStickerUpload} className="quick-upload-form">
                <div className="form-group">
                  <label>Upload Images *</label>
                  <div 
                    className={`multi-file-upload-area ${dragActive ? 'drag-active' : ''}`}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                      const files = Array.from(e.dataTransfer.files);
                      const imageFiles = files.filter(file => file.type.startsWith('image/'));
                      setQuickUploadFiles([...quickUploadFiles, ...imageFiles]);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragActive(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                    }}
                  >
                    {quickUploadFiles.length > 0 ? (
                      <div className="selected-files">
                        <p>{quickUploadFiles.length} files selected</p>
                        <div className="file-previews">
                          {quickUploadFiles.map((file, index) => (
                            <div key={index} className="file-preview-item">
                              {file.type.startsWith('image/') ? (
                                <img 
                                  src={URL.createObjectURL(file)} 
                                  alt={file.name}
                                  className="preview-thumbnail"
                                />
                              ) : (
                                <div className="file-icon">📁</div>
                              )}
                              <span className="file-name">{file.name}</span>
                              <button
                                type="button"
                                className="remove-file-btn"
                                onClick={() => {
                                  const newFiles = quickUploadFiles.filter((_, i) => i !== index);
                                  setQuickUploadFiles(newFiles);
                                }}
                                aria-label="Remove file"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <div className="add-more-files">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => {
                                const newFiles = Array.from(e.target.files);
                                const imageFiles = newFiles.filter(file => file.type.startsWith('image/'));
                                setQuickUploadFiles([...quickUploadFiles, ...imageFiles]);
                              }}
                              className="file-input"
                              id="additional-quick-sticker-files"
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="additional-quick-sticker-files" className="btn btn-sm btn-secondary">
                              + Add More
                            </label>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <div className="upload-icon">🎨</div>
                        <p>Drag and drop multiple images here, or click to select</p>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files);
                            const imageFiles = files.filter(file => file.type.startsWith('image/'));
                            setQuickUploadFiles(imageFiles);
                          }}
                          className="file-input"
                          id="quick-sticker-files"
                          style={{ display: 'none' }}
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => triggerFileInput('#quick-sticker-files')}
                        >
                          Choose Files
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => {
                    setShowQuickStickerUpload(false);
                    setQuickUploadCategory(null);
                    setQuickUploadFiles([]);
                  }} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setQuickUploadFiles([])}
                    className="btn btn-secondary"
                    disabled={quickUploadFiles.length === 0}
                  >
                    Clear All
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={uploading || quickUploadFiles.length === 0}>
                    {uploading ? 'Uploading...' : `Upload ${quickUploadFiles.length} Files`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* General Sticker Upload Modal */}
      {showGeneralStickerUpload && (
        <div className="modal-overlay">
          <div className="modal-content add-sticker-modal">
            <div className="modal-header">
              <h3>Upload Stickers</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowGeneralStickerUpload(false);
                  setGeneralUploadFormData({ categoryId: '' });
                  setSelectedFiles([]);
                  setFilePreviews([]);
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleGeneralStickerUpload}>
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={generalUploadFormData.categoryId}
                  onChange={(e) => setGeneralUploadFormData({...generalUploadFormData, categoryId: e.target.value})}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Sticker Files *</label>
                <div 
                  className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  {filePreviews.length > 0 ? (
                    <div className="file-previews">
                      {filePreviews.map((item, index) => (
                        <div key={index} className="file-preview-item">
                          {item.preview ? (
                            <img src={item.preview} alt={`Preview ${index}`} />
                          ) : (
                            <div className="file-placeholder">
                              📁 {item.file.name}
                            </div>
                          )}
                          <button 
                            type="button" 
                            className="remove-file-btn"
                            onClick={() => removeFile(index)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <div className="add-more-files">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleFileInput}
                          className="file-input"
                          id="additional-general-files"
                        />
                        <label htmlFor="additional-general-files" className="btn btn-sm btn-secondary">
                          + Add More
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <div className="upload-icon">🎨</div>
                      <p>Drag and drop sticker images here, or click to select</p>
                      <p className="upload-hint">You can select multiple files at once</p>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileInput}
                        className="file-input"
                        id="general-sticker-files"
                      />
                      <label htmlFor="general-sticker-files" className="btn btn-primary">
                        Choose Files
                      </label>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowGeneralStickerUpload(false);
                    setGeneralUploadFormData({ categoryId: '' });
                    setSelectedFiles([]);
                    setFilePreviews([]);
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setSelectedFiles([]);
                    setFilePreviews([]);
                  }}
                  className="btn btn-secondary"
                  disabled={selectedFiles.length === 0}
                >
                  Clear All
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                  disabled={uploading || selectedFiles.length === 0}
                >
                  {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Sticker${selectedFiles.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.show && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={hideSnackbar}
        />
      )}

    </div>
  );
};

export default Stickers;
